import Agriculture from "@mui/icons-material/Agriculture";
import Person from "@mui/icons-material/Person";
import React from "react";
import type { BillableTool } from "~api/BillableTool.ts";
import { IconWithLabel } from "~components/shared/Icon/IconWithLabel.tsx";
import { useResources } from "~contexts/ResourceContext/ResourceContext.tsx";
import type { Employee, Tool } from "~generated";
import { Resource as ApiResource } from "~generated";
import { classList } from "~lib/classList.ts";
import type {
  Resource,
  ResourceWithDisplayName,
} from "~lib/resourceHelpers.ts";
import {
  getResourceName,
  isApiResource,
  isEmployee,
  isTool,
} from "~lib/resourceHelpers.ts";

import styles from "./ResourceName.module.scss";

const RESOURCE_ICON_MAPPING = {
  [ApiResource.type.EMPLOYEE]: Person,
  [ApiResource.type.TOOL]: Agriculture,
};

interface SharedResourceNameProps {
  bold?: boolean;
  showIcon?: boolean;
  strikeThrough?: boolean;
}

interface ResourceNameProps extends SharedResourceNameProps {
  resource: Employee | BillableTool | Tool | ResourceWithDisplayName | Resource;
}

function ResourceName({
  resource,
  bold = false,
  showIcon = true,
  strikeThrough = false,
}: ResourceNameProps) {
  let type: ApiResource.type | undefined;
  if (isEmployee(resource)) {
    type = ApiResource.type.EMPLOYEE;
  } else if (isTool(resource)) {
    type = ApiResource.type.TOOL;
  } else if (isApiResource(resource)) {
    type = resource.type;
  } else {
    throw new Error(
      "Could not resolve Icon and name. Did you pass in an invalid resource?",
    );
  }
  const ResourceIcon = RESOURCE_ICON_MAPPING[type];

  return (
    <IconWithLabel icon={showIcon && <ResourceIcon fontSize="small" />}>
      <span
        className={classList(
          bold && styles.bold,
          strikeThrough && styles.strikeThrough,
        )}
      >
        {getResourceName(resource)}
      </span>
    </IconWithLabel>
  );
}

interface LazyLoadResourceNameProps extends SharedResourceNameProps {
  resourceId: string | null | undefined;
}

function LazyLoadResourceName({
  resourceId,
  bold = false,
  showIcon = true,
  strikeThrough = false,
}: LazyLoadResourceNameProps) {
  const { getResource, isLoading } = useResources();

  if (!resourceId || isLoading) {
    return null;
  }

  const foundResource = getResource(resourceId);
  if (!foundResource) {
    return null;
  }

  return (
    <ResourceName
      resource={foundResource as ResourceWithDisplayName}
      bold={bold}
      showIcon={showIcon}
      strikeThrough={strikeThrough}
    />
  );
}

const MemoizedResourceName = React.memo(ResourceName);
const MemoizedLazyLoadResourceName = React.memo(
  LazyLoadResourceName,
  (prev, next) =>
    prev.resourceId === next.resourceId &&
    prev.bold === next.bold &&
    prev.showIcon === next.showIcon &&
    prev.strikeThrough === next.strikeThrough,
);

export {
  MemoizedLazyLoadResourceName as LazyLoadResourceName,
  MemoizedResourceName as ResourceName,
};
